import { useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { PANEL_CARD, PanelTitle } from './PanelCard';
import { ListingLinksTable } from './ListingLinksTable';
import { addSubmodelUrl, useSubmodels } from './listingStore';
import { CallsBadge, type CallEntry } from './CallsBadge';
import { reasoningTooltip } from './ReasoningBadge';
import { cleanListingUrl, elideHost, linkHref } from './listingUrl';
import {
  callPictures, previewCalls, renderProduct, type ListingCall, type ListingCandidate,
} from './listingSuggest';
import { searchListings, useListingSearch } from './listingSuggestStore';
import { useBrand } from './brandStore';
import { useLanguage } from '../../contexts/LanguageContext';

// One proposed link: what the page calls itself, its address, and its own picture.
//
// TWO TARGETS, because there are two things to do with a suggestion and they are not
// the same gesture. The tile is the page — hovering underlines it, clicking opens the
// shop in a new tab, which is how you find out whether this is your product. The "+"
// is the decision, and it is its own bordered button so that it looks like one: the
// row used to add on a click anywhere, which meant the only way to LOOK at a listing
// was to accept it first.
function SuggestionRow({
  candidate,
  onAdd,
  addTitle,
  openTitle,
}: {
  candidate: ListingCandidate;
  onAdd: () => void;
  addTitle: string;
  openTitle: string;
}) {
  // A thumbnail that 404s or is blocked leaves a broken-image glyph in a row that is
  // otherwise perfectly good, so it takes itself off the row instead.
  const [noImage, setNoImage] = useState(false);
  const href = linkHref(candidate.url);
  return (
    <div className="flex w-full items-center gap-2.5 rounded-lg border border-slate-200 bg-white p-1.5 transition-colors hover:border-blue-500">
      {/* `group` so hovering anywhere on the tile — the picture included — underlines
          the text, which is what says the whole tile is the link. */}
      <a
        href={href || undefined}
        target="_blank"
        rel="noopener noreferrer"
        title={openTitle}
        className="group flex min-w-0 flex-1 items-center gap-2.5"
      >
        {candidate.image && !noImage ? (
          <img
            src={candidate.image}
            alt=""
            loading="lazy"
            onError={() => setNoImage(true)}
            className="h-10 w-10 shrink-0 rounded-md object-cover"
          />
        ) : (
          <span className="h-10 w-10 shrink-0 rounded-md bg-slate-100" />
        )}
        {/* min-w-0 so the two lines elide inside the row rather than widening it, and
            centred in what is left of it once the picture has taken its corner. */}
        <span className="flex min-w-0 flex-1 flex-col text-center">
          <span className="truncate text-sm text-slate-800 group-hover:underline">
            {candidate.title || elideHost(candidate.url)}
          </span>
          <span className="truncate text-xs text-slate-400 group-hover:underline">
            {candidate.url}
          </span>
        </span>
      </a>
      <button
        type="button"
        onClick={onAdd}
        title={addTitle}
        aria-label={addTitle}
        className="shrink-0 rounded-lg border border-slate-300 p-1.5 text-slate-400 transition-colors hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600"
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
}

// "Find the links": the manual names the shop and the model, so the links can be
// looked up instead of pasted.
//
// EVERYTHING HERE IS A PROPOSAL. Nothing is saved by searching, and a wrong result
// costs a glance — which is what lets the feature be imperfect without being harmful.
// The server has already fetched every address it offers (a model asked for links
// will otherwise invent plausible ones), so these are pages that exist; whether they
// are pages of THIS product is the judgement left on screen.
//
// TWO HALVES, one search (listingSuggestStore). The badge runs it, and stands FIRST among
// Phase 1's reads — the listing reads need links, and this is where links come from. What
// it proposes stays over the link rows (ListingSuggestions, below), since that is where
// accepting one lands. It never runs on its own, not even under "Run all": the picture
// match is not reliable, and a wrong product adopted unseen would be every later phase's.
export function FindLinksBadge({
  docId,
  file,
  disabled,
}: {
  docId: string;
  file: Blob;
  disabled?: boolean;
}) {
  const { t } = useLanguage();
  const { busy, calls, pages, epoch } = useListingSearch(docId);
  const info = useBrand(docId);

  // WHAT THE BADGE WALKS: the call that was made, or — before anything has been searched —
  // the one that would be. It is there from the start, like every other phase's, because
  // "what is this about to ask" is a question you have before you press it, not only after.
  const shown: ListingCall[] = calls.length
    ? calls
    : [{ kind: 'match', prompt: '', model: '', raw: '', reasoning: '', urls: [] }];

  // With the drawing the press would send. The thumbnails are what the search finds, so
  // before one there is only the drawing to show.
  const preview = async () => {
    const [fetched, rendered] = await Promise.all([previewCalls(), renderProduct(file, info)]);
    const mine = fetched[0];
    return { prompt: mine?.prompt || '', model: mine?.model || '', urls: callPictures(rendered, [], t) };
  };

  // A call that has HAPPENED hands over the very prompt it was sent, held here rather than
  // re-asked of the route: the match prompt numbers the products that search returned, and
  // no later request could rebuild it. One that has not asks the route for the preview.
  const entries: CallEntry[] = shown.map((call, i) => ({
    key: `${epoch}-${call.kind}-${i}`,
    label: t('notice.listing.callMatch'),
    load: () => (call.prompt
      ? Promise.resolve({
        prompt: call.prompt, model: call.model, urls: callPictures(pages, call.urls, t),
      })
      : preview()),
    answer: call.raw || call.reasoning ? reasoningTooltip(call.reasoning, call.raw, t) : undefined,
  }));

  // ONE NAMED BADGE, as Phase 1's reads are: Run is the search, and its call (the pictures
  // matched) is walked in the panel.
  return (
    <CallsBadge
      title={t('notice.listing.find')}
      named
      entries={entries}
      run={{ onRun: () => void searchListings(docId, file), busy, disabled }}
    />
  );
}

// THE SEARCH'S OWN BOX, between the manual and the web listings: it searches with what the
// manual read found and fills the links the listings are read from, so it stands between
// the two. What it proposed, then the links themselves.
export function FindLinksPanel({ docId, disabled }: { docId: string; disabled?: boolean }) {
  const { t } = useLanguage();
  const submodels = useSubmodels(docId);
  const { busy, error, found, searchError } = useListingSearch(docId);
  // Why the shop gave nothing, in the reader's words. A refusal names its status.
  const [why, status] = searchError.split(':');
  const failure = searchError
    ? `${t(`notice.listing.searchError.${why}`)}${status ? ` (${status})` : ''}`
    : '';

  // Only what is not already in the list above. Compared on the CLEANED url, which is
  // what a link is stored as, so accepting a suggestion makes it leave this list.
  const fresh = (found || []).filter(
    (c) => !submodels.some((s) => s.url && s.url === cleanListingUrl(c.url)),
  );

  return (
    <div className={`${PANEL_CARD} flex flex-col items-center gap-2 px-4 pb-5 pt-3`}>
      <PanelTitle>{t('notice.listing.find')}</PanelTitle>
      {busy && <Loader2 className="h-4 w-4 animate-spin text-slate-500" />}
      {error && (
        <p className="text-center text-xs text-rose-600">
          {t(error === 'noPages' ? 'notice.listing.noPages'
            : error === 'noInfo' ? 'notice.listing.noInfo' : 'notice.listing.searchFailed')}
        </p>
      )}

      {/* A search that found nothing says so. It is a normal outcome — a manual that
          names no shop, a product no longer sold — and silence would read as a button
          that did not work.
          On `found`, NOT on `fresh`: a search whose every result has since been added
          to the list above has an empty `fresh` and found plenty, and telling somebody
          "no listing was found" underneath the four links it just gave them is the
          message contradicting the screen. */}
      {!busy && failure && <p className="text-center text-xs text-rose-600">{failure}</p>}
      {!busy && !error && !failure && found !== null && !found.length && (
        <p className="text-center text-xs text-slate-400">
          {t('notice.listing.noneFound')}
        </p>
      )}

      {!busy && fresh.length > 0 && (
        <div className="flex w-full max-w-xl flex-col gap-1.5">
          {fresh.map((c) => (
            <SuggestionRow
              key={c.url}
              candidate={c}
              onAdd={() => addSubmodelUrl(docId, c.url)}
              addTitle={t('notice.listing.addFound')}
              openTitle={t('notice.listing.open')}
            />
          ))}
        </div>
      )}
      <ListingLinksTable docId={docId} disabled={disabled} />
    </div>
  );
}
